import { useCallback } from 'react';

// Add a new interface for options
interface FormDraftOptions {
    excludeFields?: string[];
}

// Update the function to accept options
export function useFormDraft(formType: string, formId: string, options: FormDraftOptions = {}) {
    const STORAGE_KEY = formType && formId ? `form_draft_${formType}_${formId}` : '';

    // Helper function to get formatted timestamp
    const getTimestamp = () => {
        const now = new Date();
        return `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
    };

    // Update saveDraft to filter out excluded fields
    const saveDraft = useCallback((data: any) => {
        try {
            // Get excludeFields inside the callback
            const excludeFields = options.excludeFields || [];

            // Filter out excluded fields
            const filteredData = { ...data };
            excludeFields.forEach(field => {
                delete filteredData[field];
            });

            // Add timestamp to the draft data
            const draftWithTimestamp = {
                ...filteredData,
                timestamp: Date.now()
            };

            const draftData = JSON.stringify(draftWithTimestamp);
            localStorage.setItem(STORAGE_KEY, draftData);
            console.log(`[${getTimestamp()}] Draft saved:`, draftWithTimestamp);
        } catch (error) {
            console.error(`[${getTimestamp()}] Error saving draft:`, error);
        }
    }, [STORAGE_KEY, options.excludeFields]);

    // Rest of the hook remains the same
    const getDraft = useCallback(() => {
        try {
            const draftData = localStorage.getItem(STORAGE_KEY);
            if (draftData) {
                const parsed = JSON.parse(draftData);
                console.log(`[${getTimestamp()}] Draft loaded:`, parsed);
                return parsed;
            }
        } catch (error) {
            console.error(`[${getTimestamp()}] Error loading draft:`, error);
        }
        return null;
    }, [STORAGE_KEY]);

    const clearDraft = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
            console.log(`[${getTimestamp()}] Draft cleared`);
        } catch (error) {
            console.error(`[${getTimestamp()}] Error clearing draft:`, error);
        }
    }, [STORAGE_KEY]);

    // Add debounced auto-save functionality
    const startAutoSave = useCallback((getData: () => any) => {
        const timeoutId = setTimeout(() => {
            const data = getData();
            saveDraft(data);
        }, 1000); // 1 second delay

        return () => clearTimeout(timeoutId);
    }, [saveDraft]);

    // Modify startDelayedAutoSave to track if anything has actually changed
    const startDelayedAutoSave = useCallback((getData: () => any, intervalMs: number = 30000) => {
        let intervalId: number | null = null;

        // Start after 15 seconds of user making changes
        const initialDelayId = setTimeout(() => {
            // Get current values to compare with initial
            const currentValues = getData();

            // Always save regardless of changes to ensure consistent behavior
            console.log('Initial 15-second delay passed, starting auto-save');

            // First save after delay
            saveDraft(currentValues);

            // Then set up recurring saves every 30 seconds (or specified interval)
            intervalId = window.setInterval(() => {
                const updatedData = getData();
                console.log('Auto-saving draft at interval');
                saveDraft(updatedData);
            }, intervalMs);
        }, 15000);

        // Return cleanup function for the delayed start
        return () => {
            clearTimeout(initialDelayId);
            if (intervalId !== null) {
                window.clearInterval(intervalId);
            }
        };
    }, [saveDraft]);

    // Return empty implementations if formType or formId is missing
    if (!formType || !formId) {
        return {
            getDraft: () => null,
            clearDraft: () => { },
            startAutoSave: () => () => { },
            startDelayedAutoSave: () => () => { },
            stopAutoSave: () => { },
            saveDraft: () => { }
        };
    }

    return {
        getDraft,
        clearDraft,
        startAutoSave,
        startDelayedAutoSave,
        stopAutoSave: clearDraft,
        saveDraft
    };
}